import { API } from '../config'
import { AxiosResponse } from 'axios'
import { v4 as uuidv4 } from 'uuid'

export interface AnimalMovementParams {
  idMovimentacao?: string
  idAnimal: string
  idTransacao?: string
  tela?: string
  nome: string
  data?: string
  loteOrigem: string | null
  loteDestino: string | null
  pastoOrigem: string | null
  pastoDestino: string | null
  username?: string | null
}

/**
 * Serviço centralizado para gerenciamento de movimentações de animais.
 * Responsável por registrar todas as movimentações de animais no sistema
 * incluindo nascimentos, transferências, etc.
 */
export class AnimalMovementService {
  /**
   * Cria uma nova movimentação de animal
   * @param farmId - ID da fazenda
   * @param movement - Dados da movimentação
   * @returns Resposta da API com a movimentação criada
   */
  public async createAnimalMovement(farmId: string, movement: AnimalMovementParams): Promise<AxiosResponse<any>> {
    return await API.put(`/entity/AnimalMovimentacao/${farmId}`, movement)
  }

  /**
   * Cria uma movimentação de nascimento para um animal
   * @param farmId - ID da fazenda
   * @param animalId - ID do animal
   * @param animalName - Nome do animal
   * @param loteDestino - Lote de destino (geralmente o lote da mãe)
   * @param pastoDestino - Pasto de destino (geralmente o pasto da mãe)
   * @returns Resposta da API com a movimentação criada
   */
  public async createBirthMovement({
    farmId,
    animalId,
    animalName,
    loteDestino,
    pastoDestino,
    username
  }: {
    farmId: string
    animalId: string
    animalName: string
    loteDestino?: string | null
    pastoDestino?: string | null
    username?: string | null
  }): Promise<AxiosResponse<any>> {
    const movement: AnimalMovementParams = {
      idMovimentacao: uuidv4(),
      idAnimal: animalId,
      idTransacao: '',
      tela: 'nascimento',
      nome: animalName,
      data: new Date().toISOString(),
      loteOrigem: null,
      loteDestino: loteDestino || null,
      pastoOrigem: null,
      pastoDestino: pastoDestino || null,
      username: username || null
    }

    return await this.createAnimalMovement(farmId, movement)
  }

  /**
   * Cria uma movimentação de transferência para um animal
   * @param farmId - ID da fazenda
   * @param animalId - ID do animal
   * @param animalName - Nome do animal
   * @param loteOrigem - Lote de origem
   * @param loteDestino - Lote de destino
   * @param pastoOrigem - Pasto de origem
   * @param pastoDestino - Pasto de destino
   * @returns Resposta da API com a movimentação criada
   */
  public async createTransferMovement({
    farmId,
    animalId,
    animalName,
    loteOrigem,
    loteDestino,
    pastoOrigem,
    pastoDestino,
    username
  }: {
    farmId: string
    animalId: string
    animalName: string
    loteOrigem?: string | null
    loteDestino?: string | null
    pastoOrigem?: string | null
    pastoDestino?: string | null
    username?: string | null
  }): Promise<AxiosResponse<any>> {
    const movement: AnimalMovementParams = {
      idMovimentacao: uuidv4(),
      idAnimal: animalId,
      idTransacao: uuidv4(),
      nome: animalName,
      data: new Date().toISOString(),
      loteOrigem: loteOrigem || null,
      loteDestino: loteDestino || null,
      pastoOrigem: pastoOrigem || null,
      pastoDestino: pastoDestino || null,
      username: username || null
    }

    return await this.createAnimalMovement(farmId, movement)
  }
}
